import { getApiBaseUrl } from '../config/environment';

class ReviewService {
  // Get all reviews for a specific product
  async getProductReviews(productId, options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.page) params.append('page', options.page);
      if (options.limit) params.append('limit', options.limit);
      if (options.rating) params.append('rating', options.rating);
      if (options.sort) params.append('sort', options.sort);
      
      const response = await fetch(`${getApiBaseUrl()}/reviews/product/${productId}?${params}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch reviews');
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching product reviews:', error);
      throw error;
    }
  }

  // Create a new review
  async createReview(reviewData, token) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(reviewData)
      });
      
      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error('Invalid response from server');
      }
      
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to create review');
      }
      
      return data;
    } catch (error) {
      if (error.message === 'Failed to fetch') {
        throw new Error('Could not connect to server. Check if backend is running on port 5000.');
      }
      console.error('Error creating review:', error);
      throw error;
    }
  }

  // Get single review
  async getReview(reviewId) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/reviews/${reviewId}`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch review');
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching review:', error);
      throw error;
    }
  }

  // Update review
  async updateReview(reviewId, updateData, token) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/reviews/${reviewId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update review');
      }
      
      return data;
    } catch (error) {
      console.error('Error updating review:', error);
      throw error;
    }
  }

  // Delete review
  async deleteReview(reviewId, token) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/reviews/${reviewId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete review');
      }
      
      return data;
    } catch (error) {
      console.error('Error deleting review:', error);
      throw error;
    }
  }

  // Mark review as helpful
  async markHelpful(reviewId, helpful, token) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/reviews/${reviewId}/helpful`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ helpful })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to mark review helpful');
      }
      
      return data;
    } catch (error) {
      console.error('Error marking review helpful:', error);
      throw error;
    }
  }

  // Get user's reviews
  async getUserReviews(token, options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.page) params.append('page', options.page);
      if (options.limit) params.append('limit', options.limit);
      
      const response = await fetch(`${getApiBaseUrl()}/reviews/user/me?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch user reviews');
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching user reviews:', error);
      throw error;
    }
  }

  // Add admin response to review
  async addAdminResponse(reviewId, comment, token) {
    try {
      const response = await fetch(`${getApiBaseUrl()}/reviews/${reviewId}/admin-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ comment })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to add admin response');
      }
      
      return data;
    } catch (error) {
      console.error('Error adding admin response:', error);
      throw error;
    }
  }

  // Admin: Create review manually (on behalf of customer)
  async createAdminReview(productId, reviewData, token) {
    try {
      const url = `${getApiBaseUrl()}/admin/reviews`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ product: productId, ...reviewData })
      });
      
      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error('Invalid response from server');
      }
      
      if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to create review');
      }
      
      return data;
    } catch (error) {
      if (error.message === 'Failed to fetch') {
        throw new Error('Could not connect to server. Check if backend is running and CORS is configured.');
      }
      console.error('Error creating admin review:', error);
      throw error;
    }
  }

  // Get all reviews for admin (with search and filters)
  async getAllReviewsForAdmin(token, options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.page) params.append('page', options.page);
      if (options.limit) params.append('limit', options.limit);
      if (options.search) params.append('search', options.search);
      if (options.rating) params.append('rating', options.rating);
      if (options.sort) params.append('sort', options.sort);
      if (options.status) params.append('status', options.status);
      
      const response = await fetch(`${getApiBaseUrl()}/admin/reviews?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch admin reviews');
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching admin reviews:', error);
      throw error;
    }
  }
}

export default new ReviewService();
